import React from 'react';
import { Add, Delete, Edit } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { useBaseHedgeMaterials, useDerivedMaterials, useCreateDerivedMaterial, useDeleteDerivedMaterial } from '../../../hooks/useBaseHedgeMaterials';
import { AddDerivedMaterialModal } from './AddDerivedMaterialModal';
import { EditDerivedRatioModal } from './EditDerivedRatioModal';
import { DerivedHedgeMaterial } from '../../../services/baseHedgeMaterialsService';
import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { useFormulasPrices } from '../../../hooks/useFormulasPrices';

interface Props {
  accountId: number;
}

export const HedgesDerivedMaterialsSection: React.FC<Props> = ({ accountId }) => {
  
  const { materialsMap } = useMaterialsStore();
  const { data: formulasPricesData } = useFormulasPrices();
  const { data: baseHedgeMaterials, isLoading: isLoadingBase, error: baseError } = useBaseHedgeMaterials(accountId);
  
  const baseTemplate = baseHedgeMaterials?.[0]; // Get first (default) template
  
  const templateId = baseTemplate?.id ? parseInt(baseTemplate.id, 10) : undefined;
  const { data: derivedMaterials, isLoading: isLoadingDerived, error: derivedError } = useDerivedMaterials(templateId);
  
  const createDerivedMutation = useCreateDerivedMaterial();
  const deleteDerivedMutation = useDeleteDerivedMaterial();
  
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [editing, setEditing] = React.useState<DerivedHedgeMaterial | null>(null);

  // Reset mutation on mount to clear any failed state
  React.useEffect(() => {
    createDerivedMutation.reset();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const handleAddDerived = async (formulaCode: string, priceRatio: number, linkedRatioFormulaId?: string) => {
    if (!baseTemplate) return;
    
    // Temporary hardcoded mapping of formula codes to numeric IDs
    // TODO: Replace with proper formula lookup service
    const formulaCodeToId: Record<string, number> = {
      'LCC': 30,
      'RFVCBLEUD': 47,
      'RFVCBLEURAD': 48,
      'RFVCLLEUD': 49,
      'RFVCLLEURAD': 50,
    };

    const numericFormulaId = formulaCodeToId[formulaCode];
    if (!numericFormulaId) {
      return;
    }

    // Convert linked ratio formula code to numeric ID
    const linkedRatioId = linkedRatioFormulaId ? formulaCodeToId[linkedRatioFormulaId] : null;

    createDerivedMutation.mutate({
      baseHedgeMaterialId: parseInt(baseTemplate.id, 10),
      payload: {
        derived_formula_id: numericFormulaId,
        price_ratio: priceRatio,
        allocation_percent: 0, // Will be calculated automatically
        linked_ratio_formula_id: linkedRatioId,
      },
    });
  };

  const handleDeleteDerived = (derivedId: number) => {
    if (!baseTemplate) return;
    deleteDerivedMutation.mutate({
      derivedMaterialId: derivedId,
      baseHedgeMaterialId: parseInt(baseTemplate.id, 10),
    });
  };

  if (isLoadingBase) {
    return <div className="px-4 py-3 text-sm text-gray-500">Loading...</div>;
  }

  if (!baseTemplate) {
    return (
      <div className="px-4 py-3 text-sm text-gray-500">
        No base hedge material configured for this account
      </div>
    );
  }

  const baseMaterial = materialsMap[baseTemplate.formula_code];

  return (
    <div className="space-y-4">
      {/* Base Material Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-blue-900">Base Material:</span>
          <span className="text-sm text-blue-800 font-medium">{baseTemplate.formula_name}</span>
          <span className="text-xs text-blue-600">({baseTemplate.formula_code})</span>
          {baseTemplate.base_contract_size_mt && baseTemplate.unit && (
            <span className="text-xs text-blue-700 ml-auto">
              {baseTemplate.base_contract_size_mt} {baseTemplate.unit} per quote
            </span>
          )}
        </div>
      </div>

      {/* Derived Materials Section */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-sm font-semibold text-gray-700">Derived Materials</h5>
          <Tooltip title="Add derived material">
            <IconButton size="small" onClick={() => setShowAddModal(true)}>
              <Add fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>

        {isLoadingDerived ? (
          <div className="text-xs text-gray-500">Loading derived materials...</div>
        ) : derivedMaterials && derivedMaterials.length > 0 ? (
          <div className="space-y-2">
            {derivedMaterials.map((derived) => (
              <div
                key={derived.id}
                className="bg-gray-50 border border-gray-200 rounded p-2 flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {derived.formula_name}
                  </div>
                  {(() => {
                    // Show the same ratio the derivation uses: linked → latest price of the
                    // linked ratio (stored value only if it never had a price), unlinked → stored.
                    const live = derived.linked_ratio_code ? formulasPricesData?.pricesMap?.[derived.linked_ratio_code] : undefined;
                    const hasLive = !!live?.price && live.price > 0;
                    const effectiveRatio = hasLive ? live!.price : Number(derived.price_ratio);
                    const d = live?.priceDate ? String(live.priceDate).slice(0, 10).split('-').reverse().join('.') : '';
                    return (
                      <div className="text-xs text-gray-600 flex items-center gap-1.5 flex-wrap mt-0.5">
                        <span>Ratio <strong className="text-gray-900">{effectiveRatio.toFixed(2)}</strong></span>
                        {derived.linked_ratio_code ? (
                          <span className="rounded-full px-2" style={{ background: hasLive ? '#eff6ff' : '#fffbeb', color: hasLive ? '#1d4ed8' : '#b45309' }}>
                            {hasLive ? `Live · ${derived.linked_ratio_name ?? derived.linked_ratio_code}${d ? ` · ${d}` : ''}` : 'Live · no price yet (stored value)'}
                          </span>
                        ) : (
                          <span className="rounded-full px-2" style={{ background: '#f1f5f9', color: '#475569' }}>Fixed</span>
                        )}
                      </div>
                    );
                  })()}
                  {Number(derived.allocation_percent) > 0 && (
                    <div className="text-xs text-gray-500">
                      Allocation: {(Number(derived.allocation_percent) * 100).toFixed(1)}%
                    </div>
                  )}
                </div>
                <Tooltip title="Edit ratio">
                  <IconButton size="small" onClick={() => setEditing(derived)}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteDerived(derived.id)}
                    disabled={deleteDerivedMutation.isLoading}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-gray-500 italic">
            No derived materials added yet. Click + to add one.
          </div>
        )}
      </div>

      <EditDerivedRatioModal open={!!editing} onClose={() => setEditing(null)} derived={editing} />

      {/* Add Derived Material Modal */}
      <AddDerivedMaterialModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddDerived}
        baseFormulaId={baseTemplate.formula_code}
        existingDerivedIds={derivedMaterials?.map((d) => d.formula_code) || []}
      />
    </div>
  );
};
